import React from "react";
import {
  Loader,
  LoaderSizes,
  Button,
  Iconography,
  IconographySizes,
  Pagination,
  Notification,
  NotificationVariants,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../redux";
import { userSliceAction } from "../../../../../redux/slices/user";
import {
  getUsersByTypeSagaAction,
  sendAccountInviteSagaAction,
} from "../../../../../redux/saga/user/saga-actions";
import { UserListType } from "../../../../../redux/slices/user/Types";
import UserManagementToolbar from "../components/UserManagementToolbar";
import UserManagementList from "../components/UserManagementList";
import { getPartnerDropdownSagaAction } from "../../../../../redux/saga/partner-details/saga-actions";
import PartnerUserFormModal from "../../../../../common/components/PartnerUserFormModal";
import { useFilteredUserList } from "../hooks/useFilteredUserList";

interface PartnerUsersTabProps {
  readOnly?: boolean;
}

const PartnerUsersTab: React.FC<PartnerUsersTabProps> = ({
  readOnly = false,
}) => {
  const [searchTerm, setSearchTerm] = React.useState("");
  const [currentPageNumber, setCurrentPageNumber] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);
  const [addModalOpen, setAddModalOpen] = React.useState(false);
  const [updateModalOpen, setUpdateModalOpen] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState<UserListType[0] | null>(
    null
  );
  const [toasts, setToasts] = React.useState<any[]>([]);

  const dispatch = useAppDispatch();
  const { userListPartnerGlobal, status } = useAppSelector((state) => state.user);
  const { partnerDropdownOptions } = useAppSelector(
    (state) => state.partner_details
  );

  const partnerNameById = React.useMemo(() => {
    const map: Record<number, string> = {};
    partnerDropdownOptions.forEach((option) => {
      map[option.value] = option.label;
    });
    return map;
  }, [partnerDropdownOptions]);

  React.useEffect(() => {
    dispatch(getUsersByTypeSagaAction("partner"));
    dispatch(getPartnerDropdownSagaAction());
  }, [dispatch]);

  React.useEffect(() => {
    setCurrentPageNumber(1);
  }, [searchTerm]);

  React.useEffect(() => {
    if (
      status.sendAccountInviteStatus !== "idle" &&
      status.sendAccountInviteMessage
    ) {
      const isSuccess = status.sendAccountInviteStatus === "success";
      setToasts([
        {
          title: isSuccess ? "Account Invite Sent" : "Error",
          message: status.sendAccountInviteMessage,
          variant: isSuccess
            ? NotificationVariants.Success
            : NotificationVariants.Error,
          icon: (
            <Iconography
              icon={isSuccess ? "check" : "ban"}
              size={IconographySizes.Medium}
            />
          ),
          expanded: true,
        },
      ]);
      dispatch(userSliceAction.resetSendAccountInviteStatus());
    }
  }, [
    status.sendAccountInviteStatus,
    status.sendAccountInviteMessage,
    dispatch,
  ]);

  const onAddNewUser = React.useCallback(() => {
    setUpdateModalOpen(false);
    setSelectedUser(null);
    setAddModalOpen(true);
  }, []);

  const handleUpdateModalOpen = React.useCallback(
    (open: React.SetStateAction<boolean>) => {
      setUpdateModalOpen(open);
      if (open === false) {
        setSelectedUser(null);
      }
    },
    []
  );

  const filteredUsers = useFilteredUserList(userListPartnerGlobal, searchTerm);
  const totalRecords = filteredUsers.length;
  const paginatedData = filteredUsers.slice(
    (currentPageNumber - 1) * pageSize,
    currentPageNumber * pageSize
  );

  const renderModals = () => (
    <>
      {addModalOpen && (
        <PartnerUserFormModal
          setModalOpen={setAddModalOpen}
          mode="Add"
          allowPartnerSelect
          listRefreshTarget="partner-global"
        />
      )}
      {updateModalOpen && selectedUser && (
        <PartnerUserFormModal
          key={selectedUser.ID}
          user={selectedUser}
          setModalOpen={handleUpdateModalOpen}
          mode="Update"
          allowPartnerSelect
          listRefreshTarget="partner-global"
        />
      )}
    </>
  );

  if (status.userListPartnerGlobalLoading && userListPartnerGlobal.length === 0) {
    return (
      <>
        <div className="um-tab__loading">
          <Loader variant={LoaderSizes.large} />
        </div>
        {renderModals()}
      </>
    );
  }

  if (userListPartnerGlobal.length === 0) {
    return (
      <>
        <div className="um-tab">
          <Notification toasts={toasts} setToasts={setToasts} />
          <div className="um-tab__empty">
            <Iconography icon="users" size={IconographySizes.Large} />
            <p>No Partner Users Added</p>
            <Button
              variant="primary"
              label="Add New User"
              className="user-management__tab-action-btn"
              icon={
                <Iconography icon="plus" size={IconographySizes.Medium} />
              }
              onClick={onAddNewUser}
              disabled={readOnly}
            />
          </div>
        </div>
        {renderModals()}
      </>
    );
  }

  return (
    <>
      <div className="um-tab">
        <Notification toasts={toasts} setToasts={setToasts} />
        <UserManagementToolbar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onAddUser={onAddNewUser}
          addButtonDisabled={readOnly}
          searchDisabled={addModalOpen || updateModalOpen}
        />
        <div className="table-card">
          <UserManagementList
            users={paginatedData}
            readOnly={readOnly}
            showPartnerColumn
            partnerNameById={partnerNameById}
            actions={{
              onEdit: (user) => {
                if (readOnly) return;
                const freshUser =
                  userListPartnerGlobal.find(
                    (listUser) => listUser.ID === user.ID
                  ) ?? user;
                setSelectedUser(freshUser);
                setAddModalOpen(false);
                setUpdateModalOpen(true);
              },
              onInvite: (user) => {
                if (readOnly) return;
                dispatch(
                  sendAccountInviteSagaAction({
                    ID: user.ID,
                    userEmail: user.email,
                  })
                );
              },
              inviteLoading: status.sendAccountInviteLoading,
            }}
          />
          <Pagination
            currentPageNumber={currentPageNumber}
            totalRecords={totalRecords}
            pageSize={pageSize}
            border={true}
            onPageNumberClick={setCurrentPageNumber}
            onPageSizesClick={(_e, value) => {
              setPageSize(Number(value));
              setCurrentPageNumber(1);
            }}
            pageSizes={[
              { label: "20", value: "20" },
              { label: "50", value: "50" },
              { label: "100", value: "100" },
            ]}
          />
        </div>
      </div>
      {renderModals()}
    </>
  );
};

export default PartnerUsersTab;
